<template>
  <div class="account-create-listing-checkout">
    <h1 class="h3 mb-3">
      Checkout
    </h1>
    <h2 class="h5 mb-3">
      Summary
    </h2>
    <b-card class="mb-4">
      <div>
        <h3 class="h5">
          {{ listing.title }}
        </h3>
        <hr>
        <p>
          Listing Fee:
          <strong>
            {{ listingFee | currency }}
          </strong>
        </p>
        <hr>
        <p>
          Your listing will be available on ZoopLocal for 60 days.
          If it is not sold within that time, you will be given an option to renew.
        </p>
      </div>
      <hr/>
      <div>
        <strong>Total: {{ listingFee | currency }}</strong>
      </div>
    </b-card>
    <div class="mb-3">
      <h2 class="h5">
        Payment Method
      </h2>
      <p>
        Your card is only charged when your listing is approved.
        Fees paid for listing items on ZoopLocal are non-refundable.
        Once a listing has been posted and the transaction fee paid,
        no refunds or credits will be issued, regardless of whether the item is sold,
        removed by the seller, or remains unsold.
      </p>
    </div>
    <b-alert variant="danger" :show="cardErrorMessage !== null">
      <strong>Error:</strong> {{ cardErrorMessage }}
    </b-alert>
    <b-card class="mb-3">
      <b-form>
        <b-row>
          <b-col
            :cols="12">
            <b-form-group
              label="Your Email">
              <b-form-input
                v-model="user.email"
                id="form-input-email"
                disabled>
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            :cols="12">
            <b-form-group
              label="Card Details">
              <div class="card-element-wrapper">
                <div id="card-element"></div>
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
    <div class="mb-3 text-right">
      <b-button
        variant="primary"
        squared
        @click="submit">
        Submit for Review
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import currency from '@/filters/currency.filter'

const stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)

export default {
  name: 'Checkout',
  filters: {
    currency,
  },
  data() {
    return {
      listing: {
        id: null,
        title: null,
      },
      listingFee: 0,
      payment: {
        name: '',
        number: '',
        expiration: '',
        cvv: '',
      },
      clientSecret: '',
      cardElement: null,
      cardErrorMessage: null,
    }
  },
  methods: {
    async submit() {
      this.cardErrorMessage = null

      try {
        const paymentResult = await stripe.confirmCardPayment(this.clientSecret, {
          payment_method: {
            card: this.cardElement,
          },
        })

        if (paymentResult.error) {
          Sentry.captureException(paymentResult.error)
          console.error(paymentResult.error)
          this.cardErrorMessage = paymentResult.error.message
          return
        }
        await this.$http.post(`/listing/${this.listing.id}/submit`, {
          paymentIntentId: paymentResult.paymentIntent.id,
        })
        this.$router.push('/account/listings/pending')
      } catch (err) {
        console.log(err)
        this.$bvToast.toast('There was an error processing your payment method. Please try again.', {
          title: 'Error',
          variant: 'danger',
        })
      }
    },
    async setUpStripe() {
      const { data: { clientSecret } } = await this.$http.get(`/listing/${this.listing.id}/checkout-session`)
      this.clientSecret = clientSecret

      const style = {
        base: {
          color: '#212529',
          fontFamily: 'Raleway, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#6c757d',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      }
      const elements = stripe.elements()
      this.cardElement = elements.create('card', { style })
      this.cardElement.mount('#card-element')
      this.cardElement.on('change', () => {
        this.cardErrorMessage = null
      })
    },
  },
  computed: {
    ...mapGetters('Authentication', [
      'user',
    ]),
  },
  async created() {
    try {
      const { data: { listing } } = await this.$http.get(`/account/listing/${this.$route.query.id}`)
      this.listing = listing
    } catch (err) {
      console.log(err)
    }

    try {
      const { data: { listings: listingFee } } = await this.$http.get('fees')
      this.listingFee = listingFee
    } catch (err) {
      console.log(err)
    }

    this.setUpStripe()
  },
}
</script>

<style lang="scss" scoped>
.card-element-wrapper {
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px;
}
</style>
